import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { catchError, map, mergeMap, of, from } from "rxjs";
import drawingsService, { DrawingDto } from "../services/DrawingsService";
import filesService, { type SharedItemsResponse } from "@/pages/FilePage/services/files.service";
import { mapApiToFolderRecord, type FilePermissions } from "@/pages/FilePage/utils/fileUtils";
import type { FileRecord } from "@/common/define";
import { fileActions } from "@/store/file/fileSlice";

interface DrawingsResult {
  drawings: DrawingDto[];
}

interface DrawingsState {
  result: DrawingsResult | null;
  loading: boolean;
  error?: string;
  sheetData: any | null;
  sheetLoading: boolean;
  sheetError?: string;
  sharedItems: FileRecord[];
  sharedItemsLoading: boolean;
  sharedItemsError?: string;
  currentFolderPermissions: FilePermissions | null;
  sharedItemsPagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

const initialState: DrawingsState = {
  result: null,
  loading: false,
  sheetData: null,
  sheetLoading: false,
  sharedItems: [],
  sharedItemsLoading: false,
  currentFolderPermissions: null,
  sharedItemsPagination: {
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  },
};

const drawingsSlice = createSlice({
  name: "drawings",
  initialState,
  reducers: {
    getAllFileDrawing: (state, _action: PayloadAction<{ projectId: number }>) => {
      state.loading = true;
      state.error = undefined;
    },
    getAllFileDrawingSuccess: (state, action: PayloadAction<DrawingDto[]>) => {
      state.loading = false;
      state.result = {
        drawings: action.payload ?? [],
      };
    },
    getAllFileDrawingFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
        getDrawingSheetJson: (state, _action: PayloadAction<{ sheetPath: string }>) => {
      state.sheetLoading = true;
      state.sheetError = undefined;
    },
    getDrawingSheetJsonSuccess: (state, action: PayloadAction<any>) => {
      state.sheetLoading = false;
      state.sheetData = action.payload;
    },
    getDrawingSheetJsonFailure: (state, action: PayloadAction<string>) => {
      state.sheetLoading = false;
      state.sheetError = action.payload;
    },
    // Shared items actions
    getSharedItemsRequest: (state, _action: PayloadAction<{ page?: number; pageSize?: number }>) => {
      state.sharedItemsLoading = true;
      state.sharedItemsError = undefined;
    },
    getSharedItemsSuccess: (state, action: PayloadAction<{ items: FileRecord[]; pagination: any }>) => {
      state.sharedItemsLoading = false;
      state.sharedItems = action.payload.items;
      state.sharedItemsPagination = {
        page: action.payload.pagination.page,
        pageSize: action.payload.pagination.pageSize,
        totalCount: action.payload.pagination.queryCount,
        totalPages: action.payload.pagination.pageCount,
      };
    },
    getSharedItemsFailure: (state, action: PayloadAction<string>) => {
      state.sharedItemsLoading = false;
      state.sharedItemsError = action.payload;
    },
    setCurrentFolderPermissions: (state, action: PayloadAction<FilePermissions | null>) => {
      state.currentFolderPermissions = action.payload;
    },
    setSharedItemsLoading: (state, action: PayloadAction<boolean>) => {
      state.sharedItemsLoading = action.payload;
    },
  },
});

export const drawingsActions = drawingsSlice.actions;
export default drawingsSlice.reducer;

// --- Epic ---
export const getAllFileDrawingEpic = (action$: any) =>
  action$.pipe(
    ofType(drawingsActions.getAllFileDrawing.type),
    mergeMap((action: any) =>
      drawingsService.getAllFileDrawing(action.payload.projectId).pipe(
        map((res:any) =>
          drawingsActions.getAllFileDrawingSuccess(res)
        ),
        catchError((err) =>
          of(drawingsActions.getAllFileDrawingFailure(err.message ?? "Error"))
        )
      )
    )
  );

export const getDrawingSheetJsonEpic = (action$: any) =>
  action$.pipe(
    ofType(drawingsActions.getDrawingSheetJson.type),
    mergeMap(async (action: any) => {
      const res = await drawingsService.getDrawingSheetJson(action.payload.sheetPath);
      return drawingsActions.getDrawingSheetJsonSuccess(res);
    })
  );

export const getSharedItemsEpic = (action$: any) =>
  action$.pipe(
    ofType(drawingsActions.getSharedItemsRequest.type),
    mergeMap((action: any) => {
      const { page = 1, pageSize = 20 } = action.payload || {};
      
      return filesService.getSharedItems(page, pageSize).pipe(
        mergeMap((response: SharedItemsResponse) => {
          // Filter items có userId !== ownerId
          const filteredItems = response.results.filter(
            (item) => item.userId !== item.ownerId
          );

          // Transform và map qua mapApiToFolderRecord
          const mappedItems = filteredItems.map((item) => {
            let transformedItem: any;
            
            if (item.document) {
              const doc = item.document;
              transformedItem = {
                id: doc.id,
                name: doc.name,
                type: doc.type || 'file',
                version: doc.version,
                size: doc.size,
                owner: doc.owner,
                createUser: doc.owner,
                lastModified: doc.lastModified,
                bucketName: doc.bucketName || '',
                cloudLink: doc.cloudLink || '',
                fileId: doc.fileId,
                fileCode: doc.fileCode,
                jsonContent: doc.jsonContent,
                isPublish: doc.isPublish,
                parentId: doc.parentId,
                createDate: doc.createDate,
                fileType: doc.fileType,
                permissions: item.permissions, // Include permissions from shared item
                raw: item
              };
            } else if (item.label) {
              const lbl = item.label;
              transformedItem = {
                id: lbl.id,
                name: lbl.name,
                type: 'folder',
                version: '1',
                size: null,
                owner: lbl.createUser,
                createUser: lbl.createUser,
                lastModified: lbl.lastModified,
                bucketName: '',
                cloudLink: '',
                color: lbl.color,
                labelCode: lbl.labelCode,
                createdDate: lbl.createdDate,
                users: lbl.users,
                clientId: lbl.clientId,
                labelChildren: lbl.labelChildren,
                documentChildren: lbl.documentChildren,
                fileType: lbl.fileType,
                permissions: item.permissions, // Include permissions from shared item
                raw: item
              };
            }

            return transformedItem ? transformedItem : null;
          }).filter(Boolean) as FileRecord[];

          return from([
            fileActions.setPagination({
              page: response.page,
              pageCount: response.pageCount,
              pageSize: response.pageSize,
              queryCount: response.queryCount,
              firstRowIndex: (response.page - 1) * response.pageSize + 1,
              lastRowIndex: Math.min(
                response.page * response.pageSize,
                response.queryCount
              ),
            }),
            drawingsActions.getSharedItemsSuccess({
              items: mappedItems,
              pagination: response
            })
          ]);
        }),
        catchError((err) =>
          of(
            drawingsActions.getSharedItemsFailure(
              err.message ?? "Error loading shared items"
            )
          )
        )
      );
    })
  );

export const drawingsEpic = [getAllFileDrawingEpic, getDrawingSheetJsonEpic, getSharedItemsEpic];
